import type { EditorEdge, EditorNode } from "./editor-types";
import type { SnapRect } from "./node-snapping";

export type CanvasHandleSide = "top" | "right" | "bottom" | "left";

export interface ConnectionHandles {
  sourceHandle: CanvasHandleSide;
  targetHandle: CanvasHandleSide;
}

/** Chooses the shortest facing pair from the relative node centres. */
export function bestConnectionHandles(source: SnapRect, target: SnapRect): ConnectionHandles {
  const sourceCenter = {
    x: source.x + source.width / 2,
    y: source.y + source.height / 2,
  };
  const targetCenter = {
    x: target.x + target.width / 2,
    y: target.y + target.height / 2,
  };
  const deltaX = targetCenter.x - sourceCenter.x;
  const deltaY = targetCenter.y - sourceCenter.y;

  if (Math.abs(deltaX) >= Math.abs(deltaY)) {
    return deltaX >= 0
      ? { sourceHandle: "right", targetHandle: "left" }
      : { sourceHandle: "left", targetHandle: "right" };
  }
  return deltaY >= 0
    ? { sourceHandle: "bottom", targetHandle: "top" }
    : { sourceHandle: "top", targetHandle: "bottom" };
}

/** Re-evaluates only edges incident to nodes whose bounds changed. */
export function reassignConnectionHandles(
  edges: readonly EditorEdge[],
  nodeRects: ReadonlyMap<string, SnapRect>,
  changedNodeIds?: ReadonlySet<string>,
): EditorEdge[] {
  let changed = false;
  const next = edges.map((edge) => {
    if (changedNodeIds && !changedNodeIds.has(edge.source) && !changedNodeIds.has(edge.target)) return edge;
    const source = nodeRects.get(edge.source);
    const target = nodeRects.get(edge.target);
    if (!source || !target) return edge;
    const handles = bestConnectionHandles(source, target);
    if (edge.sourceHandle === handles.sourceHandle && edge.targetHandle === handles.targetHandle) return edge;
    changed = true;
    return { ...edge, ...handles };
  });
  return changed ? next : edges as EditorEdge[];
}
